import { useMemo } from "react";
import { Label, SortedDependencyMap } from "../../autoTrigger.types";
import { SeparatorType } from "../Separator";
import {
  COLUMN_BG_PADDING,
  COLUMN_GAP,
  COLUMN_WIDTH,
} from "./chartView.constants";
import { ColumnLane, LayoutEdge, LayoutNode } from "./chartView.types";

const HEADER_HEIGHT = 36;
const TRIGGER_NODE_HEIGHT = 44;
const ORDER_NODE_HEIGHT = 48;
const NODE_GAP = 12;

const getShortHeaderTitle = (day: number, isNumbered?: boolean): string => {
  if (day === 0) return isNumbered ? "0" : "Order Start";
  if (day > 0) return `+${day}`;
  return `${day}`;
};

const getHeaderType = (day: number): SeparatorType => {
  if (day === 0) return SeparatorType.OrderStart;
  if (day > 0) return SeparatorType.AfterOrder;
  return SeparatorType.PriorToOrder;
};

interface UseChartLayoutParams {
  sortedDependencyMap: SortedDependencyMap;
  labels: Label[];
  orderTemplateName: string | null;
  isNumbered?: boolean;
}

/**
 * Derive dependency edges from labels.
 */
const buildDependencyGraph = (labels: Label[]) => {
  const predecessorsOf = new Map<string, Set<string>>();
  const edgeColors = new Map<string, string>();

  labels.forEach((l) => {
    if (l.fromMapId !== l.mapId && l.displayOnMapId === l.fromMapId) {
      const principalId = l.fromMapId;
      const predecessorId = l.mapId;

      const existing = predecessorsOf.get(principalId);
      if (existing) {
        existing.add(predecessorId);
      } else {
        predecessorsOf.set(principalId, new Set([predecessorId]));
      }
      edgeColors.set(`${predecessorId}->${principalId}`, l.labelColor);
    }
  });

  return { predecessorsOf, edgeColors };
};

/**
 * Build an orthogonal SVG path that routes the vertical segment
 * through the gap between column backgrounds, never through a column.
 */
const buildEdgePath = (
  source: { x: number; colIndex: number },
  targetColIndex: number,
  targetX: number,
  sourceY: number,
  targetY: number,
  totalCols: number
): string => {
  if (source.colIndex === targetColIndex) {
    // Same column — route through the nearest gap
    if (targetColIndex < totalCols - 1) {
      // Use the gap to the right
      const exitX = source.x + COLUMN_WIDTH;
      const gapX = source.x + COLUMN_WIDTH + COLUMN_GAP / 2;
      const enterX = targetX + COLUMN_WIDTH;
      return `M ${exitX} ${sourceY} H ${gapX} V ${targetY} H ${enterX}`;
    }
    if (targetColIndex > 0) {
      // Last column — use the gap to the left
      const exitX = source.x;
      const gapX = source.x - COLUMN_GAP / 2;
      const enterX = targetX;
      return `M ${exitX} ${sourceY} H ${gapX} V ${targetY} H ${enterX}`;
    }
    // Single column, no gaps — straight vertical fallback
    const midX = source.x + COLUMN_WIDTH / 2;
    return `M ${midX} ${sourceY} V ${targetY}`;
  }

  if (source.colIndex < targetColIndex) {
    // Left → right: vertical in the gap just left of target column
    const exitX = source.x + COLUMN_WIDTH;
    const gapX = targetX - COLUMN_GAP / 2;
    return `M ${exitX} ${sourceY} H ${gapX} V ${targetY} H ${targetX}`;
  }

  // Right → left: vertical in the gap just right of target column
  const exitX = source.x;
  const enterX = targetX + COLUMN_WIDTH;
  const gapX = enterX + COLUMN_GAP / 2;
  return `M ${exitX} ${sourceY} H ${gapX} V ${targetY} H ${enterX}`;
};

export const useChartLayout = ({
  sortedDependencyMap,
  labels,
  orderTemplateName,
  isNumbered,
}: UseChartLayoutParams) => {
  return useMemo(() => {
    const nodes: LayoutNode[] = [];
    const edges: LayoutEdge[] = [];
    const columnLanes: ColumnLane[] = [];

    const days = Object.keys(sortedDependencyMap)
      .map(Number)
      .sort((a, b) => a - b);

    if (days.length === 0)
      return {
        nodes,
        edges,
        columnLanes,
        containerWidth: 0,
      };

    // Derive dependency graph from labels
    const { predecessorsOf, edgeColors } = buildDependencyGraph(labels);

    // Track positioned nodes for edge computation
    const nodePositions = new Map<
      string,
      { x: number; y: number; height: number; colIndex: number }
    >();

    // Track bottom Y of each placed node for cascading
    const nodeBottomY = new Map<string, number>();
    let prevColumnsMaxBottom = COLUMN_BG_PADDING + HEADER_HEIGHT + NODE_GAP;
    const colNextY = new Map<number, number>();
    const colBottomY = new Map<number, number>();

    // Global offset so everything has positive positions (no negative left)
    const offsetX = COLUMN_BG_PADDING;
    const offsetY = COLUMN_BG_PADDING;

    // Place headers
    days.forEach((day, colIndex) => {
      const x = colIndex * (COLUMN_WIDTH + COLUMN_GAP) + offsetX;
      const y = offsetY;

      nodes.push({
        id: `header-${day}`,
        type: "columnHeader",
        x,
        y,
        data: {
          title: getShortHeaderTitle(day, isNumbered),
          type: getHeaderType(day),
        },
      });

      colNextY.set(colIndex, y + HEADER_HEIGHT + NODE_GAP);
      colBottomY.set(colIndex, y + HEADER_HEIGHT);

      columnLanes.push({
        x: x - COLUMN_BG_PADDING,
        width: COLUMN_WIDTH + COLUMN_BG_PADDING * 2,
      });
    });

    // Place triggers column by column
    days.forEach((day, colIndex) => {
      const x = colIndex * (COLUMN_WIDTH + COLUMN_GAP) + offsetX;
      const triggers = sortedDependencyMap[day];

      const headerBottom = offsetY + HEADER_HEIGHT + NODE_GAP;
      const startY = Math.max(headerBottom, prevColumnsMaxBottom);
      colNextY.set(colIndex, startY);

      triggers.forEach((trigger) => {
        let y = colNextY.get(colIndex) ?? startY;

        // Cascade: position below predecessors (derived from labels)
        const preds = predecessorsOf.get(trigger.id);
        if (preds) {
          preds.forEach((predId) => {
            const predBottom = nodeBottomY.get(predId);
            if (predBottom !== undefined) {
              y = Math.max(y, predBottom + NODE_GAP);
            }
          });
        }

        const triggerLabels = labels.filter(
          (l) => l.displayOnMapId === trigger.id && l.mapId === trigger.id
        );

        nodes.push({
          id: trigger.id,
          type: "trigger",
          x,
          y,
          data: { title: trigger.dependent.name, labels: triggerLabels },
        });

        nodePositions.set(trigger.id, {
          x,
          y,
          height: TRIGGER_NODE_HEIGHT,
          colIndex,
        });

        const bottom = y + TRIGGER_NODE_HEIGHT;
        nodeBottomY.set(trigger.id, bottom);
        colNextY.set(colIndex, bottom + NODE_GAP);
        colBottomY.set(
          colIndex,
          Math.max(colBottomY.get(colIndex) ?? 0, bottom)
        );

        // Create edges from predecessors to this trigger
        if (preds) {
          preds.forEach((predId) => {
            const source = nodePositions.get(predId);
            if (source) {
              const color =
                edgeColors.get(`${predId}->${trigger.id}`) ?? "#94a3b8";
              const sY = source.y + source.height / 2;
              const tY = y + TRIGGER_NODE_HEIGHT / 2;

              const path = buildEdgePath(
                source,
                colIndex,
                x,
                sY,
                tY,
                days.length
              );

              edges.push({
                id: `edge-${predId}-${trigger.id}`,
                path,
                color,
              });
            }
          });
        }
      });

      // Order template node at day 0
      if (day === 0 && orderTemplateName) {
        const y = colNextY.get(colIndex) ?? startY;
        nodes.push({
          id: "order-template",
          type: "orderTemplate",
          x,
          y,
          data: { name: orderTemplateName },
        });
        const bottom = y + ORDER_NODE_HEIGHT;
        colNextY.set(colIndex, bottom + NODE_GAP);
        colBottomY.set(
          colIndex,
          Math.max(colBottomY.get(colIndex) ?? 0, bottom)
        );
      }

      prevColumnsMaxBottom = Math.max(
        prevColumnsMaxBottom,
        colBottomY.get(colIndex) ?? 0
      );
    });

    const containerWidth =
      days.length * (COLUMN_WIDTH + COLUMN_GAP) -
      COLUMN_GAP +
      COLUMN_BG_PADDING * 2 +
      offsetX;

    return { nodes, edges, columnLanes, containerWidth };
  }, [sortedDependencyMap, labels, orderTemplateName]);
};
